import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import { logo } from '../data/content';

const servicesMenu = {
  label: 'Services',
  items: [
    { label: 'Sales Infrastructure', to: '/services' },
    { label: 'Sales Advisory', to: '/sales-advisory' },
  ],
};

const links = [
  { label: 'Founder-Led Sales', to: '/founder-led-sales' },
  { label: 'About', to: '/about' },
  { label: 'Testimonials', to: '/testimonials' },
  { label: 'Insights', to: '/insights' },
];

const isActive = (pathname, to) => pathname === to || (to !== '/' && pathname.startsWith(to));

function DesktopDropdown({ pathname }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const active = servicesMenu.items.some(i => isActive(pathname, i.to));

  useEffect(() => {
    const onDoc = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  useEffect(() => setOpen(false), [pathname]);

  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" data-testid="nav-services-dropdown-trigger" aria-haspopup="true" aria-expanded={open} onClick={() => setOpen(o => !o)}
        className={`nav-link inline-flex items-center gap-1.5 text-[13px] tracking-[0.14em] uppercase transition-colors duration-300 ${active ? 'text-[#B5876C] is-active' : 'text-[#3f3c37] hover:text-[#B5876C]'}`}>
        {servicesMenu.label}
        <ChevronDown size={14} strokeWidth={1.5} className={`transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`absolute left-1/2 -translate-x-1/2 top-full pt-4 transition-[opacity,transform] duration-300 ${open ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-1 pointer-events-none'}`}>
        <div data-testid="nav-services-dropdown-menu" className="min-w-[240px] bg-[#EBE4DE] border border-[#d9d2ca] shadow-[0_12px_30px_-12px_rgba(46,44,40,0.25)] py-2">
          {servicesMenu.items.map(i => (
            <Link key={i.to} to={i.to} data-testid={`nav-dropdown-${i.to.slice(1)}`}
              className={`block px-5 py-3 text-[12px] tracking-[0.16em] uppercase transition-colors duration-300 ${pathname === i.to ? 'text-[#B5876C]' : 'text-[#3f3c37] hover:text-[#B5876C] hover:bg-[#e4dcd4]'}`}>
              {i.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileServices({ onNavigate }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button type="button" data-testid="mobile-services-toggle" aria-expanded={open} onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between text-sm tracking-[0.14em] uppercase text-[#3f3c37]">
        {servicesMenu.label}
        <ChevronDown size={16} strokeWidth={1.5} className={`transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-3 ml-1 pl-4 border-l border-[#B5876C]/40 space-y-3">
          {servicesMenu.items.map(i => (
            <Link key={i.to} to={i.to} onClick={onNavigate} data-testid={`mobile-nav-${i.to.slice(1)}`} className="block text-[13px] tracking-[0.14em] uppercase text-[#3f3c37]">{i.label}</Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-[background,backdrop-filter,border-color] duration-500 ${scrolled ? 'bg-[#EBE4DE]/90 backdrop-blur-md border-b border-[#d9d2ca]' : 'bg-[#EBE4DE]/60 backdrop-blur-sm border-b border-transparent'}`}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between h-28 lg:h-32">
        <Link to="/" className="flex items-center gap-3" aria-label="Founder Growth Collective home">
          <img src={logo} alt="Founder Growth Collective logo" className="h-20 lg:h-24 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          <DesktopDropdown pathname={location.pathname} />
          {links.map(l => {
            const active = isActive(location.pathname, l.to);
            return (
              <Link key={l.to} to={l.to} className={`nav-link text-[13px] tracking-[0.14em] uppercase transition-colors duration-300 ${active ? 'text-[#B5876C] is-active' : 'text-[#3f3c37] hover:text-[#B5876C]'}`}>
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden lg:block">
          <Button asChild className="bg-[#2E2C28] hover:bg-[#B5876C] text-[#EBE4DE] rounded-none px-6 h-11 text-[12px] tracking-[0.18em] uppercase font-medium transition-colors duration-500 press">
            <Link to="/contact">Book a Discovery Call</Link>
          </Button>
        </div>

        <button className="lg:hidden text-[#2E2C28]" onClick={() => setOpen(!open)} aria-label="Toggle menu" data-testid="mobile-menu-toggle">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#EBE4DE] border-t border-[#d9d2ca] px-6 py-6 space-y-4">
          <MobileServices onNavigate={() => setOpen(false)} />
          {links.map(l => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block text-sm tracking-[0.14em] uppercase text-[#3f3c37]">{l.label}</Link>
          ))}
          <Button asChild className="w-full bg-[#2E2C28] hover:bg-[#B5876C] text-[#EBE4DE] rounded-none h-11 text-xs tracking-[0.18em] uppercase">
            <Link to="/contact" onClick={() => setOpen(false)}>Book a Discovery Call</Link>
          </Button>
        </div>
      )}
    </header>
  );
}
